// util/API.js
import axios from 'axios';

let onUnauthorized = null;
let isHandlingUnauthorized = false;

export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

const resolveApiBaseUrl = () => {
  const envBase = String(
    process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || ""
  ).trim();
  if (envBase) return envBase;

  if (typeof window !== "undefined") {
    const host = String(window.location?.hostname || "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") {
      return "http://localhost:8080/api";
    }
  }

  return "https://api.cherrfy.com/api";
};

const API = axios.create({
  baseURL: resolveApiBaseUrl(),
  withCredentials: true,  // Send cookies with every request
  headers: {
    'Content-Type': 'application/json',
  },
});

API.interceptors.request.use((config) => {
  // Choose token based on context:
  // - Admin routes: adminToken ONLY
  // - User routes: userToken ONLY
  // Also: respect explicit Authorization header overrides (e.g. health checks).
  const adminToken = localStorage.getItem('adminToken');
  const userToken = localStorage.getItem('userToken');

  const hasExplicitAuthHeader =
    Boolean(config?.headers) &&
    Object.prototype.hasOwnProperty.call(config.headers, 'Authorization');
  if (hasExplicitAuthHeader) {
    return config;
  }

  const url = String(config?.url || '');
  const pathname = typeof window !== 'undefined' ? String(window.location?.pathname || '') : '';
  const isAdminContext =
    pathname.startsWith('/admin') ||
    pathname.startsWith('/administrator') ||
    pathname.startsWith('/manage-admin') ||
    url.startsWith('/admin') ||
    url.startsWith('/administrator') ||
    url.startsWith('/manage-admin') ||
    url.startsWith('/partner');

  const token = isAdminContext ? adminToken : userToken;
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const hadAuthHeader = Boolean(error?.config?.headers?.Authorization);
    if ((status === 401 || status === 419) && hadAuthHeader) {
      if (!isHandlingUnauthorized) {
        isHandlingUnauthorized = true;
        try {
          const hadAdminToken = Boolean(localStorage.getItem('adminToken'));
          // Clear any stored tokens/credentials
          localStorage.removeItem('adminToken');
          localStorage.removeItem('userToken');
          sessionStorage.clear();
          // Trigger app-level handler or fallback redirect
          if (onUnauthorized) {
            const isAdminPath =
              typeof window !== 'undefined' &&
              (window.location.pathname?.startsWith('/admin') ||
                window.location.pathname?.startsWith('/administrator') ||
                window.location.pathname?.startsWith('/manage-admin'));
            onUnauthorized(isAdminPath ? 'admin' : 'user');
          } else if (typeof window !== 'undefined') {
            const isAdminPath =
              window.location.pathname?.startsWith('/admin') ||
              window.location.pathname?.startsWith('/administrator') ||
              window.location.pathname?.startsWith('/manage-admin');
            window.location.href = hadAdminToken || isAdminPath ? '/admin/auth/login' : '/login';
          }
        } finally {
          // Debounce multiple rapid 401s
          setTimeout(() => { isHandlingUnauthorized = false; }, 1000);
        }
      }
    }
    return Promise.reject(error);
  }
);

const PARTNER_API_PREFIX = '/partner';

const normalizeApiPath = (path) => {
  const text = String(path || '').trim();
  if (!text) return '/';
  return text.startsWith('/') ? text : `/${text}`;
};

const buildPartnerPath = (path) => {
  const normalizedPath = normalizeApiPath(path);
  if (normalizedPath === PARTNER_API_PREFIX || normalizedPath.startsWith(`${PARTNER_API_PREFIX}/`)) {
    return normalizedPath;
  }
  return `${PARTNER_API_PREFIX}${normalizedPath}`;
};

const resolveApiOrigin = () => {
  const baseURL = String(API.defaults.baseURL || '').trim();
  if (!baseURL || baseURL.startsWith('/')) return '';

  try {
    const url = new URL(baseURL);
    return url.origin;
  } catch (_) {
    return '';
  }
};

export const resolvePartnerFileUrl = (fileUrl) => {
  const text = String(fileUrl || '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  return `${resolveApiOrigin()}${text.startsWith('/') ? text : `/${text}`}`;
};

export const partnerApiRequest = async ({ method = 'get', path = '/', params, data }) => {
  return API.request({
    method,
    url: buildPartnerPath(path),
    ...(params ? { params } : {}),
    ...(data ? { data } : {}),
  });
};

export default API;
